<template>
  <div class="sst-root">
    <div class="sst-wrapper">
      <div class="sst-card">
        <div class="text-center mb-4">
          <img :src="$brand.logo" :alt="$brand.name" class="sst-logo" />
          <h5 class="sst-title mt-3">Check application status</h5>
          <p class="sst-sub">Enter the reference code from your signup confirmation, or the contact email you used.</p>
        </div>

        <div v-if="!result" class="mb-3">
          <label class="auth-label">Reference code OR contact email</label>
          <div class="input-group">
            <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
            <input v-model="query" type="text" class="form-control"
                   placeholder="e.g. narok-county-government  or  contact@narok.go.ke"
                   @keyup.enter="check" autofocus />
          </div>

          <div v-if="errorMsg" class="alert alert-danger py-2 px-3 mt-3"><i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}</div>

          <button class="btn btn-primary w-100 mt-3" @click="check" :disabled="!query.trim() || loading">
            <span v-if="loading"><span class="spinner-border spinner-border-sm me-1"></span> Looking up…</span>
            <span v-else><i class="mdi mdi-magnify me-1"></i> Check status</span>
          </button>
        </div>

        <!-- RESULT -->
        <div v-if="result" class="sst-result">
          <div class="text-center mb-3">
            <i :class="statusIcon" :style="{color: statusColor}" class="sst-status-icon"></i>
            <h5 :style="{color: statusColor}" class="mt-2 mb-1">{{ statusHeadline }}</h5>
            <code class="sst-code">{{ result.org_code }}</code>
          </div>

          <div class="sst-meta-grid">
            <div><b>Organisation:</b> {{ result.org_name }}</div>
            <div><b>Status:</b> <span :class="`badge bg-${statusBadge}`">{{ result.status }}</span></div>
            <div v-if="result.approved_at"><b>Approved at:</b> {{ fmtDate(result.approved_at) }}</div>
            <div v-if="result.rejected_at"><b>Rejected at:</b> {{ fmtDate(result.rejected_at) }}</div>
          </div>

          <div v-if="result.rejection_reason" class="alert alert-secondary py-2 px-3 mt-3">
            <strong>Reason given:</strong> {{ result.rejection_reason }}
          </div>

          <div class="sst-next-step mt-3">{{ nextStep }}</div>

          <div class="d-flex gap-2 mt-3">
            <button class="btn btn-outline-secondary flex-grow-1" @click="reset">
              <i class="mdi mdi-magnify me-1"></i> Check another
            </button>
            <button v-if="result.status === 'active'" class="btn btn-primary flex-grow-1" @click="$router.push('/login')">
              <i class="mdi mdi-login me-1"></i> Sign in
            </button>
          </div>
        </div>

        <div class="text-center mt-4">
          <a href="javascript:void(0)" class="sst-link" @click="$router.push('/signup')">New application →</a>
          <span class="text-muted mx-2">·</span>
          <a href="javascript:void(0)" class="sst-link" @click="$router.push('/login')">Sign in</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";

export default {
  name: "OrgSignupStatus",
  data() {
    return { query: "", loading: false, errorMsg: "", result: null };
  },
  computed: {
    statusColor() {
      switch (this.result?.status) {
        case "active":           return "#16a34a";
        case "pending_approval": return "#f59e0b";
        case "warning":          return "#f59e0b";
        case "suspended":        return "#dc2626";
        case "rejected":         return "#6b7280";
        default:                 return "#6b7280";
      }
    },
    statusBadge() {
      return ({active:"success",pending_approval:"warning",warning:"warning",suspended:"danger",rejected:"secondary"})[this.result?.status] || "secondary";
    },
    statusIcon() {
      switch (this.result?.status) {
        case "active":           return "mdi mdi-check-decagram sst-status-icon";
        case "pending_approval": return "mdi mdi-clock-outline sst-status-icon";
        case "warning":          return "mdi mdi-alert-outline sst-status-icon";
        case "suspended":        return "mdi mdi-pause-circle sst-status-icon";
        case "rejected":         return "mdi mdi-close-circle sst-status-icon";
        default:                 return "mdi mdi-help-circle sst-status-icon";
      }
    },
    statusHeadline() {
      switch (this.result?.status) {
        case "active":           return "Approved — you can sign in";
        case "pending_approval": return "Still under review";
        case "warning":          return "Active (action recommended)";
        case "suspended":        return "Subscription suspended";
        case "rejected":         return "Application was not approved";
        default:                 return "Unknown status";
      }
    },
    nextStep() {
      switch (this.result?.status) {
        case "active":           return "Your account is active. Sign in with the username and password you set during signup.";
        case "pending_approval": return "Our team is reviewing your application. We usually decide within 1–2 business days. You will be emailed at the address you provided once a decision is made.";
        case "warning":          return `Your subscription needs attention — please contact ${this.$brand.name} to resolve.`;
        case "suspended":        return "Service is paused. Please contact our finance team to resolve the issue and reactivate.";
        case "rejected":         return `Please contact ${this.$brand.name} if you can provide additional documentation. You may also submit a fresh application with corrected information.`;
        default:                 return "";
      }
    }
  },
  methods: {
    fmtDate(s) { return s ? String(s).replace("T"," ").substring(0,16) : "—"; },
    reset() { this.result = null; this.query = ""; this.errorMsg = ""; },
    async check() {
      this.errorMsg = "";
      this.loading = true;
      try {
        const p = new URLSearchParams();
        p.append("function", "getOrganizationStatus");
        const q = this.query.trim();
        if (q.includes("@")) p.append("contact_email", q);
        else                 p.append("org_code", q);
        const r = await axios.post(hms + "?function=getOrganizationStatus", p);
        if (r.data && r.data.success) {
          this.result = r.data.data;
        } else {
          this.errorMsg = (r.data && r.data.message) || "We could not find that application.";
        }
      } catch (e) {
        this.errorMsg = "Connection error. Please try again.";
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.sst-root { background: var(--hms-bg2); min-height: 100vh; }
.sst-wrapper { display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; }
.sst-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 36px 32px; width: 100%; max-width: 480px; box-shadow: 0 12px 40px rgba(0,0,0,.06); }
.sst-logo { height: 52px; }
.sst-title { color: var(--hms-h); font-weight: 700; }
.sst-sub { color: var(--hms-m); font-size: 13px; line-height: 1.5; }
.auth-label { font-size: 12px; font-weight: 600; color: var(--hms-h); margin-bottom: 4px; display: block; }

.sst-status-icon { font-size: 56px; line-height: 1; }
.sst-code { background: var(--hms-bg3); padding: 4px 10px; border-radius: 6px; color: var(--hms-primary); font-weight: 700; font-size: 13px; }
.sst-meta-grid { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: var(--hms-h); background: var(--hms-bg2); border-radius: 10px; padding: 12px 14px; }
.sst-meta-grid b { color: var(--hms-s); font-weight: 600; margin-right: 6px; }
.sst-next-step { color: var(--hms-m); font-size: 13px; line-height: 1.5; background: var(--hms-bg2); border-left: 3px solid var(--hms-primary); padding: 10px 14px; border-radius: 6px; }

.sst-link { color: var(--hms-primary); text-decoration: none; font-size: 13px; }
.sst-link:hover { text-decoration: underline; }
</style>
